import { FC, useCallback, useEffect, useState } from 'react';
import {
  IconButton,
  TextField,
  TextFieldProps,
  useTheme,
  styled,
} from '@mui/material';
import { useDebounce } from 'usehooks-ts';
import { Close, Search } from '@mui/icons-material';

const ClearButton = styled(IconButton)({
  padding: 0,
});

type SearchFieldProps = Pick<
  TextFieldProps,
  'name' | 'id' | 'placeholder' | 'fullWidth'
> & {
  value: string;
  onSearch: (query: string) => void;
  allowEmptyQuery?: boolean;
  autoSearchOnDebounce?: boolean;
  autoSearchOnBlur?: boolean;
  debounceTime?: number;
  width?: string | number;
  iconPlacement?: 'left' | 'right';
  clearable?: boolean;
};

const SearchField: FC<SearchFieldProps> = ({
  onSearch,
  allowEmptyQuery,
  autoSearchOnDebounce,
  autoSearchOnBlur,
  debounceTime,
  value,
  iconPlacement = 'right',
  clearable = false,
  ...props
}) => {
  const theme = useTheme();

  const [query, setQuery] = useState(value);
  const debouncedQuery = useDebounce(query, debounceTime ?? 300);

  const handleSearch = useCallback(() => {
    if (!debouncedQuery.length && !allowEmptyQuery) return;

    onSearch(debouncedQuery);
  }, [debouncedQuery, allowEmptyQuery, onSearch]);

  useEffect(() => {
    if (!autoSearchOnDebounce) return;

    handleSearch();
  }, [autoSearchOnDebounce, handleSearch]);

  const showClearButton = clearable && query?.length > 0;
  const handleClear = () => {
    setQuery('');
  };

  return (
    <TextField
      {...props}
      autoComplete="off"
      size="small"
      value={query}
      type="text"
      onChange={(e) => {
        setQuery(e.target.value);
      }}
      onBlur={autoSearchOnBlur ? handleSearch : undefined}
      onKeyUp={(e) => {
        if (e.key === 'Enter') {
          handleSearch();
        }
      }}
      slotProps={{
        input: {
          startAdornment:
            iconPlacement === 'left' ? (
              showClearButton ? (
                <ClearButton size="small" onClick={handleClear}>
                  <Close
                    htmlColor={theme.palette.text.secondary}
                    fontSize="small"
                  />
                </ClearButton>
              ) : (
                <Search htmlColor={theme.palette.text.secondary} />
              )
            ) : undefined,
          endAdornment:
            iconPlacement === 'right' ? (
              showClearButton ? (
                <ClearButton size="small" onClick={handleClear}>
                  <Close
                    htmlColor={theme.palette.text.secondary}
                    fontSize="small"
                  />
                </ClearButton>
              ) : (
                <Search htmlColor={theme.palette.text.secondary} />
              )
            ) : undefined,
        },
      }}
    />
  );
};

export default SearchField;
